import { PasseriniAPI } from './apiService';
import { getFormData } from '../utils/formdata';

export const getAllTramites = async ({ search }) => {
  const response = await PasseriniAPI.get(`/tramites?search=${search}`);
  return response.data || [];
}

export const createTramite = async ({ data }) => {
  const response = await PasseriniAPI.post('/tramites', data);
  return response.data;
}

// Crear trámite con subtramites por defecto
export const createTramiteWithDefaultSubtramites = async ({ data, defaultSubtramites }) => {
  // Crear el trámite base
  const tramite = await createTramite({ data });

  // Si hay subtramites por defecto configurados, enviarlos al backend
  if (defaultSubtramites && defaultSubtramites.length > 0) {
    try {
      // Enviar al backend los subtramites que deben inicializarse
      const response = await PasseriniAPI.post(`/tramites/${tramite.id}/initialize-subtramites`, {
        subtramites: defaultSubtramites
      });

      // El backend devuelve información sobre los subtramites inicializados
      return {
        tramite,
        initializedSubtramites: response.data?.initialized || defaultSubtramites,
        errors: response.data?.errors || []
      };
    } catch (error) {
      // Si falla la inicialización de subtramites, el trámite ya fue creado
      // Solo reportamos el error pero no fallamos completamente
      console.warn('Error inicializando subtramites por defecto:', error);
      return {
        tramite,
        initializedSubtramites: [],
        errors: [`Error inicializando subtramites: ${error.message || 'Error desconocido'}`]
      };
    }
  }

  // Si no hay subtramites por defecto, solo devolver el trámite
  return {
    tramite,
    initializedSubtramites: [],
    errors: []
  };
}

export const getTramite = async ({ id }) => {
  const response = await PasseriniAPI.get(`/tramites/${id}`);
  return response.data;
}


export const updateTramite = async ({ id, data }) => {
  const response = await PasseriniAPI.put(`/tramites/${id}`, data);
  return response.data;
}

export const deleteTramite = async ({ id }) => {
  const response = await PasseriniAPI.delete(`/tramites/${id}`);
  return response.data;
}

export const updateEstadoTramite = async ({ id, estado }) => {
  const response = await PasseriniAPI.patch(`/tramites/${id}`, {
    estado
  });
  return response.data;
}

export const partialUpdateTramite = async ({ id, ...data }) => {
  const response = await PasseriniAPI.patch(`/tramites/${id}`, data);
  return response.data;
}

export const toggleBloqueoTramite = async ({ id, bloqueado, motivo }) => {
  const response = await PasseriniAPI.patch(`/tramites/${id}/bloqueo`, {
    bloqueado,
    ...(motivo !== undefined ? { motivo } : {}),
  });
  return response.data;
}

export const toggleArchivadoTramite = async ({ id, archivado, motivo }) => {
  const response = await PasseriniAPI.patch(`/tramites/${id}/archivado`, {
    archivado,
    ...(motivo !== undefined ? { motivo } : {}),
  });
  return response.data;
}

/**
 * Archiva o desarchiva varias escrituras de una vez.
 * La operación es atómica: si alguna no existe o no es accesible, no se procesa ninguna.
 */
export const archivarTramitesMasivo = async ({ ids, archivado, motivo }) => {
  const response = await PasseriniAPI.post('/tramites/archivar-masivo', {
    ids,
    archivado,
    ...(motivo ? { motivo } : {}),
  });
  return response.data;
}

export const uploadEscritura = async ({ idTramite, file, publicada }) => {
  const endpoint = publicada
    ? '/escritura/publicada'
    : '/escritura'
  const data = getFormData({ escritura: file, tramite_id: idTramite });
  const response = await PasseriniAPI.post(endpoint, data);
  return response.data;

}

export const deleteEscritura = async ({ idTramite, publicada }) => {
  const endpoint = publicada
    ? `/escritura/publicada/${idTramite}`
    : `/escritura/${idTramite}`;
  const response = await PasseriniAPI.delete(endpoint);
  return response.data;
}

//Borra el campo adicional completo con el archivo adjunto si es que lo tiene
const deleteFieldsOtherFile = async (fieldsDelete) => {
  if (!fieldsDelete) return Promise.resolve();
  return Promise.all(fieldsDelete.map(fd =>
    PasseriniAPI.delete(`/otherfiles/${fd.id}`).then(response => response)
  ));
}

//Solo borra el archivo adjunto si es que existe
const deleteFileAttachOtherFile = async (filesDelete) => {
  if (!filesDelete) return Promise.resolve();
  return Promise.all(filesDelete.map(fd =>
    PasseriniAPI.delete(`/of/attach/${fd.id}`).then(response => response)
  ));
}

export const saveOtherFiles = async (files, filesDelete, fieldsDelete) => {
  await deleteFileAttachOtherFile(filesDelete);
  await deleteFieldsOtherFile(fieldsDelete);
  if (!files) return Promise.resolve();
  const formDataList = files.map(f => getFormData(f));
  return Promise.all(formDataList.map(fd =>
    PasseriniAPI.post('/otherfiles', fd).then(response => response)
  ));
}

export const saveATM = async ({ data }) => {
  const { files, filesDelete, fieldsDelete, ...restData } = data;
  await saveOtherFiles(files, filesDelete, fieldsDelete);
  const formData = getFormData(restData);
  const response = await PasseriniAPI.post('/atm', formData);
  return response.data;
}

export const savePlanoMensura = async ({ data }) => {
  const { files, filesDelete, fieldsDelete, ...restData } = data;
  await saveOtherFiles(files, filesDelete, fieldsDelete);
  const formData = getFormData(restData);
  const response = await PasseriniAPI.post('/planomensura', formData);
  return response.data;
}

export const saveCatastral = async ({ data }) => {
  const { files, filesDelete, fieldsDelete, ...restData } = data;
  await saveOtherFiles(files, filesDelete, fieldsDelete);
  const formData = getFormData(restData);
  const response = await PasseriniAPI.post('/catastral', formData);
  return response.data;
}

export const saveReserva = async ({ data }) => {
  const { files, filesDelete, fieldsDelete, ...restData } = data;
  await saveOtherFiles(files, filesDelete, fieldsDelete);
  const formData = getFormData(restData);
  const response = await PasseriniAPI.post('/reserva', formData);
  return response.data;
}


export const savePlanoRelevamiento = async ({ data }) => {
  const { files, filesDelete, fieldsDelete, ...restData } = data;
  await saveOtherFiles(files, filesDelete, fieldsDelete);
  const formData = getFormData(restData);
  const response = await PasseriniAPI.post('/planorelevamiento', formData);
  return response.data;
}
export const saveMunicipalidad = async ({ data }) => {
  const { files, filesDelete, fieldsDelete, ...restData } = data;
  await saveOtherFiles(files, filesDelete, fieldsDelete);
  const formData = getFormData(restData);
  const response = await PasseriniAPI.post('/municipalidad', formData);
  return response.data;
}


export const saveVialidad = async ({ data }) => {
  const { files, filesDelete, fieldsDelete, ...restData } = data;
  await saveOtherFiles(files, filesDelete, fieldsDelete);
  const formData = getFormData(restData);
  const response = await PasseriniAPI.post('/vialidad', formData);
  return response.data;
}

export const saveAguas = async ({ data }) => {
  const { files, filesDelete, fieldsDelete, ...restData } = data;
  await saveOtherFiles(files, filesDelete, fieldsDelete);
  const formData = getFormData(restData);
  const response = await PasseriniAPI.post('/aguas', formData);
  return response.data;
}

export const saveExpensas = async ({ data }) => {
  const { files, filesDelete, fieldsDelete, ...restData } = data;
  await saveOtherFiles(files, filesDelete, fieldsDelete);
  const formData = getFormData(restData);
  const response = await PasseriniAPI.post('/expensas', formData);
  return response.data;
}

export const saveIrrigacion = async ({ data }) => {
  const { files, filesDelete, fieldsDelete, ...restData } = data;
  await saveOtherFiles(files, filesDelete, fieldsDelete);
  const formData = getFormData(restData);
  const response = await PasseriniAPI.post('/irrigacion', formData);
  return response.data;
}

export const getAllHistorial = async (tramiteId) => {
  const response = await PasseriniAPI.get(`/historial/${tramiteId}`);
  return response.data;
}

export const saveOtherFile = async (otherFile) => {
  const formData = getFormData(otherFile);
  const response = await PasseriniAPI.post('/otherfiles', formData);
  return response.data;
}

export const saveOtros = async ({ data }) => {
  const { files, filesDelete, fieldsDelete, ...restData } = data;
  await saveOtherFiles(files, filesDelete, fieldsDelete);
  const formData = getFormData(restData);
  const response = await PasseriniAPI.post('/otros', formData);
  return response.data;
}

export const saveEstudioResultado = async ({ data }) => {
  const { files, filesDelete, fieldsDelete, ...restData } = data;
  await saveOtherFiles(files, filesDelete, fieldsDelete);
  const formData = getFormData(restData);
  const response = await PasseriniAPI.post('/estudio-titulo', formData);
  return response.data;
}

export const savePresupuestos = async ({ data }) => {
  const { files, filesDelete, fieldsDelete, ...restData } = data;
  await saveOtherFiles(files, filesDelete, fieldsDelete);
  const formData = getFormData(restData);
  const response = await PasseriniAPI.post('/presupuesto', formData);
  return response.data;
}


export const updateSubtramiteEstado = async ({ tipo, id, estado }) => {
  const response = await PasseriniAPI.patch(`/subtramites/${tipo}/${id}/estado`, {
    estado
  });
  return response.data;
}
